import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useAuth } from './AuthContext';
import { getSupabaseClient } from '../lib/supabase';

export type ThemePreference = 'cyber' | 'clean';
export type AgentMode = 'balanced' | 'concise' | 'deep';
export type ScanConfig = Record<string, string | number | boolean>;

export interface UserPreferences {
  theme: ThemePreference;
  agentMode: AgentMode;
  scanConfig?: ScanConfig;
}

interface SaveResult {
  ok: boolean;
  message?: string;
}

interface PreferencesContextValue {
  preferences: UserPreferences;
  isLoading: boolean;
  savePreferences: (next: UserPreferences) => Promise<SaveResult>;
}

const LOCAL_KEY_PREFIX = 'ss_user_preferences';
const LEGACY_LOCAL_KEY = 'ss_user_preferences';
const BOOT_THEME_KEY = 'ss_boot_theme';

const DEFAULT_PREFERENCES: UserPreferences = {
  theme: 'clean',
  agentMode: 'balanced',
  scanConfig: {},
};

const PreferencesContext = createContext<PreferencesContextValue | null>(null);

function normalizePreferences(value: unknown): UserPreferences {
  if (!value || typeof value !== 'object') return DEFAULT_PREFERENCES;

  let maybeTheme: ThemePreference = DEFAULT_PREFERENCES.theme;
  if ('theme' in value) {
    if (value.theme === 'clean' || value.theme === 'warm') maybeTheme = 'clean';
    if (value.theme === 'cyber' || value.theme === 'black') maybeTheme = 'cyber';
  }
  const maybeAgentMode = 'agentMode' in value && (
    value.agentMode === 'balanced' ||
    value.agentMode === 'concise' ||
    value.agentMode === 'deep'
  )
    ? value.agentMode
    : 'balanced';

  const maybeScanConfig =
    'scanConfig' in value && value.scanConfig && typeof value.scanConfig === 'object'
      ? value.scanConfig as ScanConfig
      : {};

  return {
    theme: maybeTheme,
    agentMode: maybeAgentMode,
    scanConfig: maybeScanConfig,
  };
}

function storageKeyForScope(scope: string): string {
  return `${LOCAL_KEY_PREFIX}:${scope}`;
}

function readLocalPreferences(scope: string): UserPreferences {
  try {
    const raw = localStorage.getItem(storageKeyForScope(scope));
    if (!raw) return DEFAULT_PREFERENCES;
    return normalizePreferences(JSON.parse(raw));
  } catch (error) {
    console.error('[preferences] local read failed', error);
    return DEFAULT_PREFERENCES;
  }
}

function readBootPreferences(): UserPreferences {
  try {
    const bootTheme = localStorage.getItem(BOOT_THEME_KEY);
    if (bootTheme === 'cyber' || bootTheme === 'clean') {
      return normalizePreferences({ ...DEFAULT_PREFERENCES, theme: bootTheme });
    }

    const guestRaw = localStorage.getItem(storageKeyForScope('guest'));
    if (guestRaw) return normalizePreferences(JSON.parse(guestRaw));

    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key && key.startsWith(`${LOCAL_KEY_PREFIX}:user:`)) {
        const raw = localStorage.getItem(key);
        if (raw) return normalizePreferences(JSON.parse(raw));
      }
    }
  } catch (error) {
    console.error('[preferences] boot read failed', error);
  }
  return normalizePreferences({ ...DEFAULT_PREFERENCES, theme: 'cyber' });
}

function writeLocalPreferences(scope: string, value: UserPreferences) {
  try {
    localStorage.setItem(storageKeyForScope(scope), JSON.stringify(value));
    localStorage.setItem(BOOT_THEME_KEY, value.theme);
  } catch (error) {
    console.error('[preferences] local write failed', error);
  }
}

export function PreferencesProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, isAuthLoading, user } = useAuth();
  const preferenceScope = isAuthenticated && user?.id ? `user:${user.id}` : 'guest';
  const [preferences, setPreferences] = useState<UserPreferences>(() => readBootPreferences());
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    document.documentElement.dataset.theme = preferences.theme;
    document.documentElement.dataset.uiPattern = preferences.theme === 'clean' ? 'pattern-2' : 'pattern-1';
    try {
      localStorage.setItem(BOOT_THEME_KEY, preferences.theme);
    } catch {
      /* no-op */
    }
  }, [preferences.theme]);

  useEffect(() => {
    let alive = true;

    const load = async () => {
      setIsLoading(true);

      if (isAuthLoading) {
        return;
      }

      if (!isAuthenticated || !user?.id) {
        const localPreferences = readLocalPreferences('guest');
        if (alive) setPreferences(localPreferences);
        if (alive) setIsLoading(false);
        return;
      }

      const client = getSupabaseClient();
      if (!client) {
        if (alive) setPreferences(readLocalPreferences(preferenceScope));
        if (alive) setIsLoading(false);
        return;
      }

      try {
        const { data, error } = await client
          .from('user_preferences')
          .select('theme, agent_mode, settings_json')
          .eq('user_id', user.id)
          .maybeSingle();

        if (error) throw error;

        const settingsJson = data?.settings_json && typeof data.settings_json === 'object'
          ? data.settings_json as Record<string, unknown>
          : {};

        const next = normalizePreferences({
          theme: data?.theme ?? DEFAULT_PREFERENCES.theme,
          agentMode:
            ('agentMode' in settingsJson
              ? settingsJson.agentMode
              : data?.agent_mode) ?? DEFAULT_PREFERENCES.agentMode,
          scanConfig: 'scanConfig' in settingsJson ? settingsJson.scanConfig : {},
        });

        writeLocalPreferences(preferenceScope, next);
        if (alive) setPreferences(next);
      } catch (error) {
        console.error('[preferences] remote load failed', error);
        if (alive) setPreferences(readLocalPreferences(preferenceScope));
      } finally {
        if (alive) setIsLoading(false);
      }
    };

    void load();
    return () => {
      alive = false;
    };
  }, [isAuthenticated, isAuthLoading, preferenceScope, user?.id]);

  useEffect(() => {
    try {
      localStorage.removeItem(LEGACY_LOCAL_KEY);
    } catch {
      /* no-op */
    }
  }, []);

  const savePreferences = useCallback(async (next: UserPreferences): Promise<SaveResult> => {
    const merged = {
      ...preferences,
      ...next,
      scanConfig: next.scanConfig ?? preferences.scanConfig ?? {},
    };

    setPreferences(merged);
    writeLocalPreferences(preferenceScope, merged);

    if (!isAuthenticated || !user?.id) {
      return { ok: true };
    }

    const client = getSupabaseClient();
    if (!client) {
      return { ok: false, message: 'Supabase の接続設定を確認してください。' };
    }

    try {
      const { error } = await client.from('user_preferences').upsert({
        user_id: user.id,
        theme: merged.theme,
        agent_mode: merged.agentMode,
        settings_json: {
          theme: merged.theme,
          agentMode: merged.agentMode,
          scanConfig: merged.scanConfig ?? {},
        },
      }, { onConflict: 'user_id' });

      if (error) throw error;
      return { ok: true };
    } catch (error) {
      console.error('[preferences] save failed', error);
      return { ok: false, message: '個人設定の保存に失敗しました。時間をおいて再度お試しください。' };
    }
  }, [isAuthenticated, preferenceScope, preferences, user?.id]);

  const value = useMemo<PreferencesContextValue>(() => ({
    preferences,
    isLoading,
    savePreferences,
  }), [isLoading, preferences, savePreferences]);

  return (
    <PreferencesContext.Provider value={value}>
      {children}
    </PreferencesContext.Provider>
  );
}

export function usePreferences(): PreferencesContextValue {
  const context = useContext(PreferencesContext);
  if (!context) throw new Error('usePreferences must be used inside PreferencesProvider');
  return context;
}
